import React, { useState, useEffect, useCallback, useRef } from 'react'
import MyLoader from '../Spinner/MyLoader';
import { BrowserRouter as Router, Redirect, Route } from 'react-router-dom'
import '../css/Admin_css/admin-customize.css';

import axios from 'axios';
import SessionManager from '../Utils/SessionManager';
import MdlSelectMultipleStudent from '../dialog/MdlSelectMultipleStudent';
import myToast from '../Utils/MyToast';
import BackButton from '../ChildComponent/BackButton';

const SpecialCaseCreation = () => {
    const token = localStorage.getItem("token");
  const [loggedIn] = useState(token !== null);
  const [Loading, setLoading] = useState(false);
  const [MultipleStudentSelector, setMultipleStudentSelector] = useState(true);
  
  const [mSelectedStudents, setMSelectedStudents] = useState([]);
  const [mStudentsData, setMStudentsData] = useState([]);
  const [SESSION_ID, setSessionId] = useState('');
  
  // Track edited fees: { studentIndex_feeIndex: { fee_id, new_value, student_id, class_id, section_id } }
  const [editedFees, setEditedFees] = useState({});
  const [feeCollectionStatus, setFeeCollectionStatus] = useState({}); // Track fee-wise collection status
  const [savingFeeKey, setSavingFeeKey] = useState(null); // Track which fee is currently being saved
  const [submittingAll, setSubmittingAll] = useState(false); // Track if submitting all fees

  // Refs to access latest state values in callbacks
  const mStudentsDataRef = useRef([]);
  const mSelectedStudentsRef = useRef([]);
  
  // Keep refs in sync with state
  useEffect(() => {
    mStudentsDataRef.current = mStudentsData;
  }, [mStudentsData]);
  
  useEffect(() => {
    mSelectedStudentsRef.current = mSelectedStudents;
  }, [mSelectedStudents]);

  useEffect(() => {
      window.scrollTo(0, 0);
  }, []);

  const HandleShow = useCallback(() => {
    setMultipleStudentSelector(prev => !prev);
  }, []);

  const HandleClose = useCallback(() => {
    setMultipleStudentSelector(false);
  }, []);

  const HandleSelectedMultipleStudents = useCallback((selectedStudents) => {
    if(!selectedStudents || selectedStudents.length === 0) {
      myToast.notify_warning("Please select at least one student");
      return;
    }

    // Get session info - try from student, or use SessionManager
    const firstStudent = selectedStudents[0];
    let session_id = firstStudent.session_id;
    
    // If session_id not in student data, get from SessionManager
    if(!session_id) {
      const sessionData = SessionManager.getSessionDetails();
      session_id = sessionData?.session_id || SessionManager.getSessionId();
    }

    setMultipleStudentSelector(false);
    setMSelectedStudents(selectedStudents);
    setSessionId(session_id);
    setMStudentsData([]);
    setEditedFees({});

    // Process all selected students
    processMultipleStudents(selectedStudents, session_id);
  }, []);

  const processMultipleStudents = useCallback((students, session_id) => {
    setLoading(true);

    const SCHOOL_CODE = SessionManager.getSchoolCode();
    
    // Extract unique student IDs
    const studentIds = students.map(s => s.student_enrollment_no || s.student_id).filter((id, index, self) => self.indexOf(id) === index);
    
    // Get class_ids (can be multiple if students are from different classes)
    const classIds = [...new Set(students.map(s => s.class_id).filter(id => id))];
    
    // Step 1: Get fee collection status using batch endpoint
    const COLLECTION_FUNCTION = 'checkIfStudentFeeCollectedBatch.php';
    const COLLECTION_URL = process.env.REACT_APP_SERVER_NAME + process.env.REACT_APP_TARGET_DIR + COLLECTION_FUNCTION;
    
    const collectionParams = {
      student_ids: studentIds.join(','),
      class_id: classIds[0] || '',
      session_id: session_id,
      school_code: SCHOOL_CODE,
    };
    console.log("Collection Status URL:", COLLECTION_URL + '?' + new URLSearchParams(collectionParams).toString());
    
    // Step 2: Get fee structure for all students using batch endpoint
    const BATCH_FEE_FUNCTION = 'getStudentSpecialFeeBatch.php';
    const BATCH_FEE_URL = process.env.REACT_APP_SERVER_NAME + process.env.REACT_APP_TARGET_DIR + BATCH_FEE_FUNCTION;
    
    const batchFeeParams = {
      student_ids: studentIds.join(','),
      class_ids: classIds.join(','),
      session_id: session_id,
      school_code: SCHOOL_CODE,
    };
    console.log("Batch Fee URL:", BATCH_FEE_URL + '?' + new URLSearchParams(batchFeeParams).toString());
    
    // Fetch both in parallel
    Promise.allSettled([
      axios.get(COLLECTION_URL, { params: collectionParams }).then(res => res.data).catch(err => {
        console.warn("Fee collection status fetch error:", err.message);
        return { api_status: "OK", data: [] };
      }),
      axios.get(BATCH_FEE_URL, { params: batchFeeParams }).then(res => res.data).catch(err => {
        console.error("Fee structure fetch error:", err);
        throw err;
      })
    ])
    .then(([collectionResult, feeResult]) => {
      // Process fee collection status
      const feeCollectionMap = {};
      if(collectionResult.status === 'fulfilled' && collectionResult.value && collectionResult.value.api_status === "OK") {
        const collectionData = collectionResult.value.data;
        // checkIfStudentFeeCollectedBatch returns { student_id: true/false } or array of objects
        if(Array.isArray(collectionData)) {
          collectionData.forEach((item) => {
            const enrollment_no = item.student_id || item.student_enrollment_no;
            if(enrollment_no) {
              feeCollectionMap[enrollment_no] = item.fee_collected === true || item.fee_collected === 1;
            }
          });
        } else if(collectionData && typeof collectionData === 'object') {
          // Handle associative array format: { student_id: true/false }
          Object.keys(collectionData).forEach((enrollment_no) => {
            feeCollectionMap[enrollment_no] = collectionData[enrollment_no] === true || collectionData[enrollment_no] === 1;
          });
        }
      }
      console.log("Fee Collection Map:", feeCollectionMap);
      
      // Process fee structure
      if(feeResult.status === 'rejected') {
        throw feeResult.reason;
      }
      
      const feeResponse = feeResult.value;
      if(!feeResponse || feeResponse.api_status !== "OK" || !feeResponse.data) {
        throw new Error("Invalid response from getStudentSpecialFeeBatch");
      }
      
      const feeDataMap = feeResponse.data; // { student_id: [fee_details] }
      
      // Build students data using student details from selection
      const studentsData = students.map((student) => {
        const enrollment_no = student.student_enrollment_no || student.student_id;
        const student_class_id = student.class_id;
        const fees = feeDataMap[enrollment_no] || [];
        
        // Build fee collection status for this student
        const studentFeeStatus = {};
        const isFeeCollected = feeCollectionMap[enrollment_no] || false;
        fees.forEach((fee) => {
          studentFeeStatus[`${enrollment_no}_${student_class_id}_${fee.fee_id}`] = isFeeCollected;
        });
        
        return {
          student: {
            ...student,
            student_enrollment_no: enrollment_no,
            student_id: enrollment_no,
            class_id: student_class_id,
            section_id: student.section_id || 'No'
          },
          fees: fees,
          feeCollected: feeCollectionMap[enrollment_no] || false,
          feeStatus: studentFeeStatus
        };
      });
      
      return studentsData;
    })
    .then((studentsData) => {
      setLoading(false);
      
      if(studentsData && studentsData.length > 0) {
        setMStudentsData(studentsData);
        
        // Update fee collection status
        const allFeeStatus = {};
        studentsData.forEach((studentData) => {
          if(studentData?.feeStatus) {
            Object.assign(allFeeStatus, studentData.feeStatus);
          }
        });
        setFeeCollectionStatus(allFeeStatus);
      } else {
        myToast.notify_warning("No fee data found for selected students");
        setMStudentsData([]);
        setFeeCollectionStatus({});
      }
    })
    .catch((error) => {
      setLoading(false);
      console.error("Error in processMultipleStudents:", error);
      console.error("Error details:", {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status,
        url: error.config?.url,
        students: studentIds
      });
      const errorMessage = error.response?.data?.api_remark || error.message || "Error fetching student data. Please try again.";
      myToast.notify_error(errorMessage);
      setMStudentsData([]);
      setFeeCollectionStatus({});
    });
  }, []);


  const handleFeeValueChange = useCallback((studentIndex, feeIndex, fee, newValue) => {
    const key = `${studentIndex}_${feeIndex}`;
    const enrollment_no = mStudentsData[studentIndex]?.student?.student_enrollment_no || mStudentsData[studentIndex]?.student?.student_id;
    const class_id = mStudentsData[studentIndex]?.student?.class_id;
    const section_id = mStudentsData[studentIndex]?.student?.section_id || 'No';
    
    // Allow empty string for clearing the field, convert to number otherwise
    let feeValue = newValue;
    if(newValue === '' || newValue === null || newValue === undefined) {
      // Keep empty string to allow user to clear and type new value
      feeValue = '';
    } else {
      // Ensure it's a number
      feeValue = typeof newValue === 'number' ? newValue : parseFloat(newValue) || 0;
    }
    
    setEditedFees(prev => ({
      ...prev,
      [key]: {
        fee_id: fee.fee_id,
        fee_value: feeValue,
        student_id: enrollment_no,
        class_id: class_id,
        section_id: section_id,
        isSpecialCase: fee.special_case === 'Yes'
      }
    }));
  }, [mStudentsData]);

  const saveSingleFee = useCallback((studentIndex, feeIndex, fee) => {
    const key = `${studentIndex}_${feeIndex}`;
    const editedFee = editedFees[key];
    
    if(!editedFee) {
      myToast.notify_warning("No changes to save");
      return;
    }

    // Check if fee is collected
    const enrollment_no = mStudentsData[studentIndex]?.student?.student_enrollment_no || mStudentsData[studentIndex]?.student?.student_id;
    const class_id = mStudentsData[studentIndex]?.student?.class_id;
    const feeKey = `${enrollment_no}_${class_id}_${fee.fee_id}`;
    const feeCollected = feeCollectionStatus[feeKey];
    
    if(feeCollected) {
      myToast.notify_warning(`Cannot save: Fee collection or settlement is already done for ${fee.fee_name}.`);
      return;
    }

    setSavingFeeKey(key);
    const mStaffData = SessionManager.getStaffDetails();
    const STAFF_ID = mStaffData.staff_id;
    const SCHOOL_CODE = SessionManager.getSchoolCode();
    const FUNCTION_NAME = 'updateSpecialFee.php';
    const URL = process.env.REACT_APP_SERVER_NAME + process.env.REACT_APP_TARGET_DIR + FUNCTION_NAME;
    
    const params = {
      staff_id: STAFF_ID,
      student_id: editedFee.student_id,
      session_id: SESSION_ID,
      class_id: editedFee.class_id,
      section_id: editedFee.section_id,
      fee_id: editedFee.fee_id,
      fee_value_old: fee.student_fee !== undefined && fee.student_fee !== null 
        ? fee.student_fee 
        : (fee.special_value !== undefined && fee.special_value !== null 
          ? fee.special_value 
          : (fee.fee_value !== undefined && fee.fee_value !== null ? fee.fee_value : 0)),
      fee_value: editedFee.fee_value,
      school_code: SCHOOL_CODE,
    };

    axios.get(URL, { params })
    .then(res => res.data)
    .then((response) => {
      setSavingFeeKey(null);
      if(response.api_status === "OK") {
        myToast.notify_success(`${editedFee.isSpecialCase ? 'Updated' : 'Created'} successfully`);
        // Remove from edited fees
        const newEditedFees = { ...editedFees };
        delete newEditedFees[key];
        setEditedFees(newEditedFees);
        // Refresh data
        processMultipleStudents(mSelectedStudentsRef.current, SESSION_ID);
      } else {
        myToast.notify_error(response.api_remark || "Failed to save");
      }
    }).catch((error) => {
      setSavingFeeKey(null);
      console.error("Error saving fee:", error);
      myToast.notify_error("Error saving fee. Please try again.");
    });
  }, [editedFees, mStudentsData, SESSION_ID, feeCollectionStatus, processMultipleStudents]);

  const submitAllFees = useCallback(() => {
    const editedKeys = Object.keys(editedFees);
    if(editedKeys.length === 0) {
      myToast.notify_warning("No changes to submit");
      return;
    }

    // Check if any fee is collected
    for(const key of editedKeys) {
      const [studentIndex, feeIndex] = key.split('_').map(Number);
      const studentData = mStudentsData[studentIndex];
      if(!studentData) continue;
      
      const fee = studentData.fees[feeIndex];
      if(!fee) continue;
      
      const enrollment_no = studentData.student?.student_enrollment_no || studentData.student?.student_id;
      const class_id = studentData.student?.class_id;
      const feeKey = `${enrollment_no}_${class_id}_${fee.fee_id}`;
      const feeCollected = feeCollectionStatus[feeKey];
      
      if(feeCollected) {
        myToast.notify_warning(`Cannot submit: Fee collection or settlement is already done for ${fee.fee_name}.`);
        return;
      }
    }

    setSubmittingAll(true);
    const mStaffData = SessionManager.getStaffDetails();
    const STAFF_ID = mStaffData.staff_id;
    const SCHOOL_CODE = SessionManager.getSchoolCode();

    // Prepare fees array for batch API
    const feesArray = editedKeys.map(key => {
      const [studentIndex, feeIndex] = key.split('_').map(Number);
      const editedFee = editedFees[key];
      const studentData = mStudentsData[studentIndex];
      const fee = studentData?.fees[feeIndex];
      
      if(!fee || !editedFee) return null;
      
      return {
        student_id: editedFee.student_id,
        class_id: editedFee.class_id,
        section_id: editedFee.section_id,
        fee_id: editedFee.fee_id,
        fee_value: editedFee.fee_value,
        fee_value_old: fee.student_fee || fee.fee_value || fee.special_value || 0
      };
    }).filter(f => f !== null);

    if(feesArray.length === 0) {
      setSubmittingAll(false);
      myToast.notify_warning("No valid fees to submit");
      return;
    }

    // Use batch API
    const BATCH_FUNCTION = 'updateSpecialFeeBatch.php';
    const BATCH_URL = process.env.REACT_APP_SERVER_NAME + process.env.REACT_APP_TARGET_DIR + BATCH_FUNCTION;
    
    const params = {
      staff_id: STAFF_ID,
      session_id: SESSION_ID,
      school_code: SCHOOL_CODE,
      fees: JSON.stringify(feesArray)
    };
    
    console.log("Batch Submit URL:", BATCH_URL);
    console.log("Submitting fees:", feesArray.length);

    // Convert to URLSearchParams for proper form encoding
    const formData = new URLSearchParams();
    formData.append('staff_id', STAFF_ID);
    formData.append('session_id', SESSION_ID);
    formData.append('school_code', SCHOOL_CODE);
    formData.append('fees', JSON.stringify(feesArray));

    axios.post(BATCH_URL, formData, {
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded'
      }
    })
    .then(res => res.data)
    .then((response) => {
      setSubmittingAll(false);
      
      if(response.api_status === "OK" && response.data) {
        const { success, failed, total } = response.data;
        
        if(success > 0) {
          myToast.notify_success(`Successfully saved ${success} out of ${total} fee(s)`);
        }
        if(failed > 0) {
          myToast.notify_warning(`Failed to save ${failed} fee(s)`);
        }
        
        // Clear edited fees and refresh
        setEditedFees({});
        processMultipleStudents(mSelectedStudentsRef.current, SESSION_ID);
      } else {
        myToast.notify_error(response.api_remark || "Failed to submit fees");
      }
    })
    .catch((error) => {
      setSubmittingAll(false);
      console.error("Error submitting fees:", error);
      myToast.notify_error("Error submitting fees. Please try again.");
    });
  }, [editedFees, mStudentsData, SESSION_ID, feeCollectionStatus, processMultipleStudents]);

  if (loggedIn === false) {
      return <Redirect to='/Login' />;
    }
  else if (Loading) {
      return <MyLoader />
    }
    else {
      return (
        <Router>
          <switch>
            <Route path={'/AdminMain/SpecialCaseCreation'} exact render={
              () => {
                return (
                <div className="content" style={{background:'#f5f7fa', minHeight:'100vh'}}>
                  <div className="content-header" style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center'
                  }}>
                      <div className="leftside-content-header">
                        <ul className="breadcrumbs">
                        <li><i className="fa fa-home text-white" aria-hidden="true" /><a href="#">{"Special Case"}</a></li>
                        </ul>
                      </div>
                    <div style={{ marginLeft: 'auto' }}>
                      <BackButton />
                    </div>
                      </div>
                  <div style={{
                    background: '#ffffff',
                    borderRadius: '8px',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                    margin: '12px',
                    padding: '16px',
                    minHeight: 'calc(100vh - 120px)',
                    border: '1px solid #f0f0f0'
                  }}>
                    <div style={{marginBottom: '12px'}}>
                      <button 
                        onClick={HandleShow} 
                        style={{
                          width: '100%',
                          padding: '12px 20px',
                          fontSize: '14px',
                          fontWeight: 600,
                          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                          color: '#ffffff',
                          border: 'none',
                          borderRadius: '6px',
                          cursor: 'pointer',
                          boxShadow: '0 2px 8px rgba(102, 126, 234, 0.4)',
                          transition: 'all 0.3s ease'
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.transform = 'translateY(-1px)';
                          e.currentTarget.style.boxShadow = '0 4px 12px rgba(102, 126, 234, 0.5)';
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.transform = 'translateY(0)';
                          e.currentTarget.style.boxShadow = '0 2px 8px rgba(102, 126, 234, 0.4)';
                        }}
                      >
                        <i className="fa fa-users" style={{marginRight: '8px'}}></i>
                        Select Students
                      </button>
                    </div>

                    {/* Multiple Students View */}
                    {mStudentsData && mStudentsData.length > 0 ? (
                      <>
                        {Object.keys(editedFees).length > 0 && (
                          <div style={{
                            marginBottom: '12px',
                            padding: '10px 12px',
                            background: 'linear-gradient(135deg, #fef3c7 0%, #fde68a 100%)',
                            borderRadius: '6px',
                            border: '1px solid #f59e0b',
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center'
                          }}>
                            <span style={{fontSize: '14px', fontWeight: 600, color: '#92400e'}}>
                              <i className="fa fa-info-circle" style={{marginRight: '8px'}}></i>
                              {Object.keys(editedFees).length} fee(s) have unsaved changes
                            </span>
                            <button
                              onClick={submitAllFees}
                              disabled={submittingAll}
                              style={{
                                padding: '8px 16px',
                                fontSize: '13px',
                                fontWeight: 600,
                                background: submittingAll 
                                  ? '#9ca3af' 
                                  : 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                                color: '#ffffff',
                                border: 'none',
                                borderRadius: '6px',
                                cursor: submittingAll ? 'not-allowed' : 'pointer',
                                boxShadow: submittingAll ? 'none' : '0 2px 4px rgba(16, 185, 129, 0.3)',
                                transition: 'all 0.2s ease',
                                opacity: submittingAll ? 0.7 : 1
                              }}
                              onMouseEnter={(e) => {
                                if(!submittingAll) {
                                  e.currentTarget.style.transform = 'translateY(-1px)';
                                  e.currentTarget.style.boxShadow = '0 4px 8px rgba(16, 185, 129, 0.4)';
                                }
                              }}
                              onMouseLeave={(e) => {
                                if(!submittingAll) {
                                  e.currentTarget.style.transform = 'translateY(0)';
                                  e.currentTarget.style.boxShadow = '0 2px 4px rgba(16, 185, 129, 0.3)';
                                }
                              }}
                            >
                              {submittingAll ? (
                                <>
                                  <i className="fa fa-spinner fa-spin" style={{marginRight: '6px'}}></i>
                                  Submitting...
                                </>
                              ) : (
                                <>
                                  <i className="fa fa-save" style={{marginRight: '6px'}}></i>
                                  Submit All
                                </>
                              )}
                            </button>
                          </div>
                        )}

                        {mStudentsData.map((studentData, studentIndex) => {
                          const student = studentData.student;
                          const fees = studentData.fees || [];
                          const feeCollected = studentData.feeCollected;
                          const enrollment_no = student.student_enrollment_no || student.student_id;

                          return (
                            <div key={enrollment_no} style={{
                              marginBottom: '12px',
                              background: '#ffffff',
                              borderRadius: '8px',
                              boxShadow: '0 1px 6px rgba(0,0,0,0.08)',
                              overflow: 'hidden',
                              border: '2px solid #f0f0f0'
                            }}>
                              {/* Student Card Header */}
                              <div style={{
                                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                                padding: '10px 14px',
                                color: '#ffffff'
                              }}>
                                <div style={{display: 'flex', alignItems: 'center', gap: '10px'}}> 
                                  <div style={{position: 'relative', flexShrink: 0}}>
                                    <div style={{
                                      width: '55px',
                                      height: '55px',
                                      borderRadius: '50%',
                                      overflow: 'hidden',
                                      border: '2px solid rgba(255,255,255,0.3)',
                                      background: '#ffffff',
                                      display: 'flex',
                                      alignItems: 'center',
                                      justifyContent: 'center'
                                    }}>
                                      <img  
                                        src={student.student_img_path}  
                                        alt={student.stu_name}
                                        style={{
                                          width: '100%',
                                          height: '100%',
                                          objectFit: 'cover'
                                        }}
                                      />
                      </div> 
                                    <div style={{
                                      position: 'absolute',
                                      bottom: '-6px',
                                      left: '50%',
                                      transform: 'translateX(-50%)',
                                      background: 'rgba(255,255,255,0.95)',
                                      padding: '1px 6px',
                                      borderRadius: '8px',
                                      fontSize: '9px',
                                      fontWeight: 600,
                                      color: '#374151',
                                      boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
                                      whiteSpace: 'nowrap'
                                    }}>
                                      {enrollment_no}
                      </div>                
                    </div>
                                  <div style={{flex: 1, minWidth: 0}}>
                                    <h4 style={{
                                      margin: '0 0 4px 0',
                                      fontSize: '16px',
                                      fontWeight: 600,
                                      color: '#ffffff',
                                      textOverflow: 'ellipsis',
                                      overflow: 'hidden',
                                      whiteSpace: 'nowrap'
                                    }}> 
                                      {student.stu_name} 
                                    </h4>
                                    <p style={{
                                      margin: '0 0 4px 0',
                                      fontSize: '12px',
                                      color: 'rgba(255,255,255,0.9)',
                                      textOverflow: 'ellipsis',
                                      overflow: 'hidden',
                                      whiteSpace: 'nowrap'
                                    }}> 
                                      Father: <span style={{fontWeight: 500}}>{student.father_name}</span> 
                                    </p>
                                    <div style={{
                                      display: 'flex',
                                      alignItems: 'center',
                                      gap: '10px',
                                      flexWrap: 'wrap'
                                    }}>
                                      <div style={{
                                        background: 'rgba(255,255,255,0.2)',
                                        padding: '2px 8px',
                                        borderRadius: '4px',
                                        fontSize: '11px',
                                        fontWeight: 500
                                      }}>
                                        {student.section_id==='No'?`Class ${student.class_name}`:`Class ${student.class_name}:${student.section_name}`}
                    </div>
                                      <div style={{
                                        fontSize: '11px',
                                        color: 'rgba(255,255,255,0.85)'
                                      }}>
                                        SRN: <span style={{fontWeight: 600}}>{student.sr_admission_no}</span>
                      </div>
                    </div>
                      </div>
                                  <div style={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    background: 'rgba(255,255,255,0.15)',
                                    padding: '8px 12px',
                                    borderRadius: '6px',
                                    minWidth: '60px'
                                  }}>
                                    <p style={{
                                      margin: '0 0 4px 0',
                                      fontSize: '9px',
                                      textTransform: 'uppercase',
                                      letterSpacing: '0.5px',
                                      opacity: 0.9
                                    }}> 
                                      Roll No. 
                                    </p>
                                    <div style={{
                                      width: '32px',
                                      height: '32px',
                                      borderRadius: '50%',
                                      background: '#ffffff',
                                      color: '#667eea',
                                      display: 'flex',
                                      alignItems: 'center',
                                      justifyContent: 'center',
                                      fontSize: '14px',
                                      fontWeight: 700,
                                      boxShadow: '0 1px 4px rgba(0,0,0,0.15)'
                                    }}>
                                      {student.student_roll_no}
                  </div>
                  </div>
                  </div>               
                </div>

                              {/* Fee Collection Warning */}
                              {feeCollected ? (
                                <div style={{
                                  padding: '12px',
                                  background: 'linear-gradient(135deg, #fee2e2 0%, #fecaca 100%)',
                                  border: '1px solid #ef4444',
                                  borderRadius: '6px',
                                  margin: '12px',
                                  textAlign: 'center'
                                }}>
                                  <h4 style={{color: '#dc2626', margin: 0, fontSize: '14px', fontWeight: 600}}>
                                    <i className="fa fa-exclamation-triangle" style={{marginRight: '8px'}}></i>
                                    Fee collection found for this student. Cannot create/update special case fee.
                                  </h4>
                  </div>
                              ) : (
                                <div style={{
                                  padding: '12px',
                                  background: '#ffffff',
                                  marginTop: '8px'
                                }}>
                                  <div style={{
                                    display: 'grid',
                                    gridTemplateColumns: 'repeat(4, 1fr)',
                                    gap: '10px'
                                  }}>
                                    {fees && fees.length > 0 ? (
                                      fees.map((item2, index2) => {
                                        const isSpecialCase = item2.special_case === 'Yes';
                                        const feeKey = `${enrollment_no}_${student.class_id}_${item2.fee_id}`;
                                        const feeCollected = feeCollectionStatus[feeKey] || false;
                                        const editKey = `${studentIndex}_${index2}`;
                                        const editedFee = editedFees[editKey];
                                        
                                        // Get original value - check all possible fields, handle 0 properly
                                        // Get original value - properly handle 0 values
                                        const originalValue = item2.student_fee !== undefined && item2.student_fee !== null 
                                          ? parseFloat(item2.student_fee) || 0
                                          : (item2.special_value !== undefined && item2.special_value !== null 
                                            ? parseFloat(item2.special_value) || 0
                                            : (item2.fee_value !== undefined && item2.fee_value !== null ? parseFloat(item2.fee_value) || 0 : 0));
                                        
                                        // Get current value - allow empty string for editing
                                        let currentValue = originalValue;
                                        if(editedFee !== undefined) {
                                          // If editedFee exists, use its value (could be number or empty string during editing)
                                          if(editedFee.fee_value === '' || editedFee.fee_value === null || editedFee.fee_value === undefined) {
                                            currentValue = ''; // Allow empty for editing
                                          } else {
                                            currentValue = parseFloat(editedFee.fee_value) || 0;
                                          }
                                        }
                                        
                                        // Check for changes - compare numeric values, but allow empty string during editing
                                        const editedNumericValue = editedFee !== undefined && editedFee.fee_value !== '' && editedFee.fee_value !== null && editedFee.fee_value !== undefined
                                          ? (parseFloat(editedFee.fee_value) || 0)
                                          : originalValue;
                                        const hasChanges = editedFee !== undefined && editedNumericValue !== originalValue;
                                        const isSaving = savingFeeKey === editKey;
                                        
                                        return (
                                          <div 
                                            key={index2}
                                            style={{
                                              background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)',
                                              border: '2px solid #e5e7eb',
                                              borderRadius: '8px',
                                              padding: '10px',
                                              boxShadow: '0 2px 6px rgba(0,0,0,0.06)',
                                              transition: 'all 0.3s ease',
                                              position: 'relative',
                                              overflow: 'hidden',
                                              opacity: feeCollected ? 0.6 : 1
                                            }}
                                            onMouseEnter={(e) => {
                                              if(!feeCollected) {
                                                e.currentTarget.style.boxShadow = '0 4px 12px rgba(102, 126, 234, 0.2)';
                                                e.currentTarget.style.transform = 'translateY(-3px)';
                                                e.currentTarget.style.borderColor = '#667eea';
                                              }
                                            }}
                                            onMouseLeave={(e) => {
                                              if(!feeCollected) {
                                                e.currentTarget.style.boxShadow = '0 2px 6px rgba(0,0,0,0.06)';
                                                e.currentTarget.style.transform = 'translateY(0)';
                                                e.currentTarget.style.borderColor = '#e5e7eb';
                                              }
                                            }}
                                          >
                                            <div style={{
                                              position: 'absolute',
                                              top: 0,
                                              right: 0,
                                              width: '60px',
                                              height: '60px',
                                              background: 'linear-gradient(135deg, rgba(102, 126, 234, 0.1) 0%, rgba(118, 75, 162, 0.1) 100%)',
                                              borderRadius: '0 10px 0 60px',
                                              zIndex: 0
                                            }}></div>
                                            <div style={{position: 'relative', zIndex: 1}}>
                                              <div style={{
                                                color: '#374151',
                                                fontSize: '15px',
                                                fontWeight: 700,
                                                marginBottom: '8px',
                                                textAlign: 'center',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                gap: '5px'
                                              }}>
                                                <i className="fa fa-tag" style={{color: '#667eea', fontSize: '12px'}}></i>
                                                {item2.fee_name}
                                              </div>
                                              <div style={{
                                                background: '#f9fafb',
                                                borderRadius: '6px',
                                                padding: '8px',
                                                marginBottom: '6px',
                                                border: '1px solid #f0f0f0'
                                              }}>
                                                <div style={{
                                                  display: 'flex',
                                                  justifyContent: 'space-between',
                                                  alignItems: 'center'
                                                }}>
                                                  <span style={{
                                                    color: '#6b7280',
                                                    fontSize: '10px',
                                                    fontWeight: 500,
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    gap: '4px'
                                                  }}>
                                                    <i className="fa fa-circle" style={{fontSize: '5px', color: '#9ca3af'}}></i>
                                                    Class Fee
                                                  </span>
                                                  <span style={{
                                                    color: '#374151',
                                                    fontSize: '13px',
                                                    fontWeight: 700
                                                  }}>
                                                    ₹{item2.fee_value}
                                                  </span>
                                      </div>
                                     </div>
                                              <div style={{
                                                background: isSpecialCase 
                                                  ? 'linear-gradient(135deg, #ecfdf5 0%, #d1fae5 100%)' 
                                                  : 'linear-gradient(135deg, #eff6ff 0%, #dbeafe 100%)',
                                                borderRadius: '6px',
                                                padding: '8px',
                                                border: isSpecialCase 
                                                  ? '1px solid #a7f3d0' 
                                                  : '1px solid #93c5fd',
                                                marginBottom: '8px'
                                              }}>
                                                <div style={{
                                                  display: 'flex',
                                                  justifyContent: 'space-between',
                                                  alignItems: 'center',
                                                  marginBottom: '6px'
                                                }}>
                                                  <span style={{
                                                    color: isSpecialCase ? '#059669' : '#2563eb',
                                                    fontSize: '10px',
                                                    fontWeight: 700,
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    gap: '4px',
                                                    textTransform: 'uppercase',
                                                    letterSpacing: '0.5px'
                                                  }}>
                                                    <i className="fa fa-star" style={{fontSize: '7px'}}></i>
                                                    {isSpecialCase ? 'Special Fee' : 'Student Fee'}
                                                  </span>
                                                </div>
                                                <input
                                                  type="number"
                                                  value={currentValue === null || currentValue === undefined || currentValue === '' ? '' : currentValue}
                                                  onChange={(e) => {
                                                    const inputValue = e.target.value;
                                                    
                                                    // Allow user to clear the field completely
                                                    if(inputValue === '' || inputValue === null) {
                                                      // Store empty string to allow clearing, will be converted to 0 on blur if still empty
                                                      handleFeeValueChange(studentIndex, index2, item2, '');
                                                      return;
                                                    }
                                                    
                                                    // Parse the number
                                                    const numValue = parseFloat(inputValue);
                                                    
                                                    // Only update if it's a valid number (including 0)
                                                    if(!isNaN(numValue)) {
                                                      handleFeeValueChange(studentIndex, index2, item2, numValue);
                                                    }
                                                  }}
                                                  onBlur={(e) => {
                                                    // When field loses focus, ensure we have a valid number
                                                    const inputValue = e.target.value.trim();
                                                    
                                                    if(inputValue === '' || inputValue === null) {
                                                      // If still empty on blur, set to 0
                                                      handleFeeValueChange(studentIndex, index2, item2, 0);
                                                    } else {
                                                      // Validate and set the number
                                                      const numValue = parseFloat(inputValue);
                                                      if(isNaN(numValue)) {
                                                        // If invalid, reset to original value
                                                        handleFeeValueChange(studentIndex, index2, item2, originalValue);
                                                      } else {
                                                        // Set the valid number
                                                        handleFeeValueChange(studentIndex, index2, item2, numValue);
                                                      }
                                                    }
                                                  }}
                                                  disabled={feeCollected}
                                                  style={{
                                                    width: '100%',
                                                    padding: '6px 8px',
                                                    fontSize: '15px',
                                                    fontWeight: 800,
                                                    color: isSpecialCase ? '#059669' : '#2563eb',
                                                    border: hasChanges ? '2px solid #f59e0b' : '1px solid ' + (isSpecialCase ? '#a7f3d0' : '#93c5fd'),
                                                    borderRadius: '4px',
                                                    background: '#ffffff',
                                                    textAlign: 'center',
                                                    cursor: feeCollected ? 'not-allowed' : 'text',
                                                    opacity: feeCollected ? 0.5 : 1
                                                  }}
                                                  placeholder="Enter fee"
                                                />
                                                {feeCollected && (
                                                  <div style={{
                                                    marginTop: '4px',
                                                    fontSize: '9px',
                                                    color: '#dc2626',
                                                    textAlign: 'center',
                                                    fontWeight: 600
                                                  }}>
                                                    Fee Collected
                                          </div>
                                                )}
                                          </div>
                                          
                                              {/* Save/Update Button */}
                                              <div style={{
                                                display: 'flex',
                                                flexDirection: 'row',
                                                gap: '6px'
                                              }}>
                                                <button
                                                  onClick={() => saveSingleFee(studentIndex, index2, item2)}
                                                  disabled={!hasChanges || feeCollected || isSaving}
                                                  style={{
                                                    flex: 1,
                                                    padding: '6px 10px',
                                                    fontSize: '10px',
                                                    fontWeight: 600,
                                                    background: (hasChanges && !feeCollected && !isSaving)
                                                      ? (isSpecialCase 
                                                          ? 'linear-gradient(135deg, #10b981 0%, #059669 100%)' 
                                                          : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)')
                                                      : '#e5e7eb',
                                                    color: (hasChanges && !feeCollected && !isSaving) ? '#ffffff' : '#9ca3af',
                                                    border: 'none',
                                                    borderRadius: '6px',
                                                    cursor: (hasChanges && !feeCollected && !isSaving) ? 'pointer' : 'not-allowed',
                                                    transition: 'all 0.2s ease',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    gap: '6px',
                                                    opacity: (hasChanges && !feeCollected && !isSaving) ? 1 : 0.6,
                                                    boxShadow: (hasChanges && !feeCollected && !isSaving) ? '0 2px 4px rgba(102, 126, 234, 0.3)' : 'none',
                                                    position: 'relative'
                                                  }}
                                                  title={isSaving 
                                                    ? 'Saving...' 
                                                    : feeCollected 
                                                      ? `Fee collection/settlement done for ${item2.fee_name} - Save not applicable` 
                                                      : hasChanges 
                                                        ? (isSpecialCase ? `Update ${item2.fee_name}` : `Save ${item2.fee_name}`)
                                                        : "No changes to save"}
                                                >
                                                  {isSaving ? (
                                                    <>
                                                      <i className="fa fa-spinner fa-spin" style={{fontSize: '10px'}}></i>
                                                      Saving...
                                                    </>
                                                  ) : (
                                                    <>
                                                      <i className={`fa ${isSpecialCase ? 'fa-sync' : 'fa-save'}`} style={{fontSize: '10px'}}></i>
                                                      {isSpecialCase ? 'Update' : 'Save'}
                                                    </>
                                                  )}
                                                </button>
                                          </div>
                                      </div>
                                          </div>
                                        );
                                      })
                                    ) : (
                                      <div style={{
                                        gridColumn: '1 / -1',
                                        textAlign: 'center',
                                        padding: '20px',
                                        color: '#6b7280'
                                      }}>
                                        <i className="fa fa-info-circle" style={{fontSize: '24px', marginBottom: '8px', opacity: 0.3}}></i>
                                        <p style={{fontSize: '12px', margin: 0}}>No fees found for this student</p>
                                      </div>
                                    )}
                                  </div>
                                </div>
                              )}
                            </div>
                          );
                        })}
                        
                        {/* Submit All Button at Bottom */}
                        {Object.keys(editedFees).length > 0 && (
                          <div style={{
                            marginTop: '16px',
                            padding: '12px',
                            background: 'linear-gradient(135deg, #fef3c7 0%, #fde68a 100%)',
                            borderRadius: '6px',
                            border: '1px solid #f59e0b',
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center'
                          }}>
                            <span style={{fontSize: '14px', fontWeight: 600, color: '#92400e'}}>
                              <i className="fa fa-info-circle" style={{marginRight: '8px'}}></i>
                              {Object.keys(editedFees).length} fee(s) have unsaved changes
                            </span>
                            <button
                              onClick={submitAllFees}
                              disabled={submittingAll}
                              style={{
                                padding: '8px 16px',
                                fontSize: '13px',
                                fontWeight: 600,
                                background: submittingAll 
                                  ? '#9ca3af' 
                                  : 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                                color: '#ffffff',
                                border: 'none',
                                borderRadius: '6px',
                                cursor: submittingAll ? 'not-allowed' : 'pointer',
                                boxShadow: submittingAll ? 'none' : '0 2px 4px rgba(16, 185, 129, 0.3)',
                                transition: 'all 0.2s ease',
                                opacity: submittingAll ? 0.7 : 1
                              }}
                              onMouseEnter={(e) => {
                                if(!submittingAll) {
                                  e.currentTarget.style.transform = 'translateY(-1px)';
                                  e.currentTarget.style.boxShadow = '0 4px 8px rgba(16, 185, 129, 0.4)';
                                }
                              }}
                              onMouseLeave={(e) => {
                                if(!submittingAll) {
                                  e.currentTarget.style.transform = 'translateY(0)';
                                  e.currentTarget.style.boxShadow = '0 2px 4px rgba(16, 185, 129, 0.3)';
                                }
                              }}
                            >
                              {submittingAll ? (
                                <>
                                  <i className="fa fa-spinner fa-spin" style={{marginRight: '6px'}}></i>
                                  Submitting...
                                </>
                              ) : (
                                <>
                                  <i className="fa fa-save" style={{marginRight: '6px'}}></i>
                                  Submit All
                                </>
                              )}
                            </button>
                          </div>
                        )}
                      </>
                    ) : (
                      <div style={{
                        textAlign: 'center',
                        padding: '40px 20px',
                        color: '#6b7280'
                      }}>
                        <i className="fa fa-users" style={{fontSize: '36px', marginBottom: '12px', opacity: 0.3}}></i>
                        <p style={{fontSize: '14px', margin: 0}}>No students selected</p>
                        <p style={{fontSize: '12px', margin: '6px 0 0 0', opacity: 0.7}}>Click "Select Students" to begin</p>
                      </div>
                    )}

                    {MultipleStudentSelector && <MdlSelectMultipleStudent actionClose={HandleClose} actionHandleSelected={HandleSelectedMultipleStudents} />}
                  </div>
                  </div>
                )
              }
            } />
          </switch>
        </Router>
      )
  }
}

export default SpecialCaseCreation;
